/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
// @sfdc-extension-line SFDC_EXT_BOPIS
import { useShowPickupAvailable } from './use-pickup-filter';
import type { ShopperSearch } from '@/scapi';
import DynamicImageProvider from '@/providers/dynamic-image';
import { ProductTile, ProductTileProvider } from '@/components/product-tile';
import { ProductTileSkeleton } from '@/components/category-skeleton';
import { Skeleton } from '@/components/ui/skeleton';
import { responsiveImageWidthsWithRefinements, responsiveImageWidthsWithoutRefinements } from './image-widths';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];

/**
 * The grid's column rule — ONE copy, consumed by the live grid and by its skeleton.
 *
 * `1` phone / `2` from `sm` / `3` from `lg`, and **three at desktop regardless of whether the
 * refinement panel is open** (CRCB2B-87, confirmed 2026-08-04). Figma ships a four-up frame
 * (`14335:40303`) as well as the three-up (`1396:10895`); the three-up governs both panel
 * states, and collapsing the panel simply widens the tiles.
 *
 * Breakpoint choice, since Figma has no tablet frame: `lg` is where the refinement sidebar
 * appears (`lg:w-64`), so it is where this layout genuinely becomes desktop — hence `lg:3`.
 * The 2-column step starts at `sm` rather than `md` because a single column up to 768px
 * leaves a ~660px-wide product image on a landscape phone.
 *
 * The skeleton previously carried its own copy of these classes. Two copies of one rule drift;
 * keep this the only one.
 *
 * ## This rule governs BOTH listing pages, deliberately
 *
 * `ProductGrid` is consumed by the category PLP and by **search** (`_app.search.tsx`), so
 * changing it here changed search too. That was reviewed and kept (decision 2026-08-08): the two
 * pages show the same tile, and a shopper moving from a category to a search result should not
 * meet a different density. Search moved from `2/3/4/4/4` to the values below — measured in
 * Chromium at 375/640/768/1024/1440: **1 / 2 / 2 / 3 / 3** columns on both routes.
 *
 * The alternative — a `columns` prop so each page could differ — was rejected because it
 * re-creates exactly the two-copies-drift problem this constant exists to prevent. If the pages
 * ever genuinely need to differ, add the prop then rather than pre-emptively.
 */
const PRODUCT_GRID_COLUMNS = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-6';

function NoProductsMessage({
    criticalSize,
    nonCriticalSize,
    suppress,
}: {
    criticalSize: number;
    nonCriticalSize: number;
    /**
     * Set when the host is already explaining the empty result itself. CRCB2B-348 renders a
     * filters-excluded-everything message beside the applied-filter chips, and two stacked empty
     * states read as a bug — so the host suppresses this generic one rather than the grid trying
     * to work out which situation it is in.
     */
    suppress?: boolean;
}) {
    const { t } = useTranslation('common');

    if (suppress || criticalSize > 0 || nonCriticalSize > 0) {
        return null;
    }
    return (
        <div className="col-span-full text-center py-12">
            <p className="text-sm text-muted-foreground">{t('noProductsFound')}</p>
        </div>
    );
}

function NonCriticalContent({
    products,
    criticalSize,
    responsiveImageWidths,
    handleProductClick,
    suppressEmptyMessage,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable,
}: {
    products: ProductSearchHit[];
    criticalSize: number;
    responsiveImageWidths: string[];
    handleProductClick?: (product: ProductSearchHit) => void;
    suppressEmptyMessage?: boolean;
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable?: boolean;
}) {
    return (
        <DynamicImageProvider value={{ widths: responsiveImageWidths }}>
            {products.map((product) => (
                <ProductTile
                    key={product.productId}
                    product={product}
                    handleProductClick={handleProductClick}
                    showNavigationArrows
                    // @sfdc-extension-line SFDC_EXT_BOPIS
                    showPickupAvailable={showPickupAvailable}
                />
            ))}
            <NoProductsMessage
                criticalSize={criticalSize}
                nonCriticalSize={products.length}
                suppress={suppressEmptyMessage}
            />
        </DynamicImageProvider>
    );
}

function ProductGridSkeleton({ count = 8 }: { count?: number }) {
    return (
        <div className={PRODUCT_GRID_COLUMNS}>
            {Array.from({ length: count }, (_, index) => (
                <div key={`grid-skeleton-${index}`} className="space-y-3">
                    <Skeleton className="aspect-square w-full" />
                    <div className="space-y-2">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                        <Skeleton className="h-5 w-20" />
                    </div>
                </div>
            ))}
        </div>
    );
}

/**
 * ProductGrid renders product tiles in a responsive grid layout, wrapping all tiles in a shared
 * context provider to reduce hydration overhead. Instead of each tile initializing its own hooks
 * (navigate, config, translation, currency), the provider initializes them once and shares them
 * via context.
 *
 * The grid accepts synchronous data for both critical (above-the-fold) and non-critical
 * (below-the-fold) products. Critical product images are loaded with high priority and eagerly.
 * For deferred loading of non-critical products, use DeferredProductGrid.
 */
export default function ProductGrid({
    critical,
    nonCritical,
    hasRefinementsPanel = true,
    panelWidths,
    handleProductClick,
    isLoading = false,
    skeletonCount,
    suppressEmptyMessage,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable: showPickupAvailableProp,
}: {
    critical?: ProductSearchHit[];
    nonCritical?: ProductSearchHit[];
    hasRefinementsPanel?: boolean;
    /**
     * Panel-open `sizes` hints for a host whose panel is not the 264px the shared set is derived
     * from. Ignored when `hasRefinementsPanel` is false. See `searchPanelImageWidths`.
     */
    panelWidths?: string[];
    handleProductClick?: (product: ProductSearchHit) => void;
    isLoading?: boolean;
    skeletonCount?: number;
    /** See `NoProductsMessage`. Set by a host that renders its own empty-result explanation. */
    suppressEmptyMessage?: boolean;
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable?: boolean;
}): ReactElement {
    const criticalData = critical ?? [];
    const l = criticalData.length;
    // `panelWidths` replaces the panel-open set only — the closed case is container-agnostic from
    // `lg` up, so every route shares it.
    const responsiveImageWidths = hasRefinementsPanel
        ? (panelWidths ?? responsiveImageWidthsWithRefinements)
        : responsiveImageWidthsWithoutRefinements;

    // Initialize the `<DynamicImageProvider/>` behavior for the scope of this grid.
    // Out-of-the-box we make sure that the product images of all products considered critical (displayed inside a
    // `<DynamicImage/>` component) should be loaded eagerly with high priority.
    const hasSource = useCallback(() => true, []);

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const pickupFromUrl = useShowPickupAvailable();
    const showPickupAvailable = showPickupAvailableProp ?? pickupFromUrl;
    // @sfdc-extension-block-end SFDC_EXT_BOPIS
    const loadingSkeletonCount = Math.max(criticalData.length + (nonCritical?.length ?? 0), 4);

    if (isLoading) {
        return (
            <ProductTileProvider>
                <div data-testid="product-grid-loading-state" aria-busy>
                    <ProductGridSkeleton count={loadingSkeletonCount} />
                </div>
            </ProductTileProvider>
        );
    }

    return (
        <ProductTileProvider>
            <div className={PRODUCT_GRID_COLUMNS}>
                {l > 0 && (
                    <DynamicImageProvider value={{ hasSource, widths: responsiveImageWidths }}>
                        {criticalData.map((product) => (
                            <ProductTile
                                key={product.productId}
                                product={product}
                                handleProductClick={handleProductClick}
                                showNavigationArrows
                                // @sfdc-extension-line SFDC_EXT_BOPIS
                                showPickupAvailable={showPickupAvailable}
                            />
                        ))}
                    </DynamicImageProvider>
                )}
                {nonCritical && nonCritical.length > 0 && (
                    <NonCriticalContent
                        products={nonCritical}
                        criticalSize={l}
                        responsiveImageWidths={responsiveImageWidths}
                        handleProductClick={handleProductClick}
                        suppressEmptyMessage={suppressEmptyMessage}
                        // @sfdc-extension-line SFDC_EXT_BOPIS
                        showPickupAvailable={showPickupAvailable}
                    />
                )}
                {(skeletonCount ?? 0) > 0 &&
                    Array.from({ length: skeletonCount ?? 0 }, (_, i) => <ProductTileSkeleton key={`skeleton-${i}`} />)}
                {!skeletonCount && (
                    <NoProductsMessage
                        criticalSize={l}
                        nonCriticalSize={nonCritical?.length ?? 0}
                        suppress={suppressEmptyMessage}
                    />
                )}
            </div>
        </ProductTileProvider>
    );
}
